import React from "react";
import FormHelpers from "../../../../helpers/FormHelpers";
import Helpers from "@Apis/helpers";
import { QUANTITY } from "@Apis/constants";

// Constants for error messages
const ERROR_MESSAGES = {
  requiredField: (fieldName) =>
    `Error in ${fieldName} field. Select/Enter a valid value then proceed.`,
  packedQuantityExceeded: (productName) =>
    `Error in Packed Quantity for Product ${productName} field. Entered quantity is more than available quantity.`,
};

// Utility to calculate available quantity
const calculateAvailableQuantity = (item, allStock) => {
  if (item?.bundle_products?.length) {
    const availableQuantities = item.bundle_products.map((bundleProduct) => {
      const availableQty = Helpers.getValueTotalInObject(
        bundleProduct?.product_batches?.filter(
          (batch) =>
            batch?.batch_in_use && (allStock ? !batch?.is_rejected_batch : true)
        ),
        "available_qty"
      );
      return availableQty / (bundleProduct.quantity / (item?.quantity || 1));
    });
    return Math.min(...availableQuantities);
  }

  return Helpers.getValueTotalInObject(
    item?.product_batches?.filter(
      (batch) =>
        batch?.batch_in_use && (allStock ? !batch?.is_rejected_batch : true)
    ),
    "available_qty"
  );
};

// Utility to validate document-level errors
const validateDocLevel = ({
  match,
  selectedCustomer,
  selectedSO,
  selectedSOForPs,
  isFromSalesOrder,
  documentUINames,
  selectedSoValue,
  location,
  tenantDepartmentId,
  packingSlipDate,
  cfPackingSlipDoc,
}) => {
  const errors = [];

  if (!match?.params?.orderId) {
    errors.push({
      name: "Customer",
      value: selectedCustomer,
      message: ERROR_MESSAGES.requiredField("Customer"),
    });
  }

  if (
    !(
      (match?.params?.orderId && selectedSO?.packing_slip_id) ||
      selectedSOForPs
    ) &&
    isFromSalesOrder
  ) {
    errors.push({
      name: documentUINames?.salesOrderUIName,
      value: selectedSoValue,
      message: ERROR_MESSAGES.requiredField(documentUINames?.salesOrderUIName),
    });
  }

  if (
    (!match?.params?.orderId || location?.state?.cloneSo) &&
    selectedSoValue
  ) {
    errors.push({
      name: "Department",
      value: tenantDepartmentId,
      message: ERROR_MESSAGES.requiredField("Department"),
    });
  }

  errors.push({
    name: "Packing Date",
    value: packingSlipDate,
    message: ERROR_MESSAGES.requiredField("Packing Date"),
  });

  if (cfPackingSlipDoc) {
    cfPackingSlipDoc.forEach((field) => {
      if (field?.isRequired && field?.isActive) {
        errors.push({
          name: field?.fieldName,
          value: field?.fieldValue,
          message: ERROR_MESSAGES.requiredField(field?.fieldName),
        });
      }
    });
  }

  return errors;
};

// Utility to validate line-level errors
const validateLineLevel = ({ data, tenantDepartmentId, allStock }) => {
  const errors = [];

  if (data && tenantDepartmentId) {
    data.forEach((item) => {
      if (!item?.product_sku_id) {
        errors.push({
          name: "Product",
          value: 0,
          message: "Error in Product column. Select a product to continue.",
        });
      } else {
        const availableQty = calculateAvailableQuantity(item, allStock) || 0;

        const normalizedQty = QUANTITY(
          (availableQty / item?.uom_info?.ratio) *
            item?.product_sku_info?.uom_info?.ratio,
          item?.uom_info?.precision || 0
        );

        errors.push({
          name: `Packed Quantity for Product ${
            item?.product_sku_info?.internal_sku_code || ""
          }`,
          value:
            Number(item?.packed_quantity) > normalizedQty
              ? 0
              : Number(item?.packed_quantity),
          message:
            Number(item?.packed_quantity) > normalizedQty
              ? ERROR_MESSAGES.packedQuantityExceeded(
                  item?.product_sku_info?.internal_sku_code || ""
                )
              : ERROR_MESSAGES.requiredField(
                  `Packed Quantity for Product ${
                    item?.product_sku_info?.internal_sku_code || ""
                  }`
                ),
        });

        item?.lineCustomFields?.forEach((customField) => {
          if (customField?.isRequired && customField?.isActive) {
            errors.push({
              name: customField?.fieldName,
              value: customField?.fieldValue,
              message: `Error in ${customField?.fieldName} of product ${item?.product_sku_info?.internal_sku_code}. Enter a value then proceed.`,
            });
          }
        });
      }
    });
  }

  return errors;
};

// Main function to get packing slip error list
const getPackingSlipErrorList = (params) => {
  const docLevelErrors = validateDocLevel(params);
  const lineLevelErrors = validateLineLevel(params);

  return FormHelpers.errorChecker({ docLevelErrors, lineLevelErrors });
};

export default getPackingSlipErrorList;
